import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { landing, landingGradient } from './landingTheme';

/**
 * Links to the sections this page actually has.
 *
 * @remarks
 * The design this follows carried a pricing entry. There is no price: this is an internal
 * platform for one organization, and a pricing page would be a link to a page that cannot
 * exist. Each entry here points at a section further down.
 */
const SECTIONS = [
  { href: '#top', label: 'خانه' },
  { href: '#features', label: 'ویژگی‌ها' },
  { href: '#how', label: 'چطور کار می‌کند' },
] as const;

export function LandingNav() {
  return (
    <Box
      id="top"
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,

        // Translucent rather than solid, so the glow behind it still reads as one field of
        // light when the page scrolls under it.
        backgroundColor: 'rgba(8, 12, 31, 0.72)',
        backdropFilter: 'blur(14px)',
        borderBottom: 1,
        borderColor: landing.border,
      }}
    >
      <Container maxWidth="lg">
        <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 1.5 }}>
          <Brand />

          <Stack
            component="nav"
            direction="row"
            spacing={1}
            sx={{ ms: 'auto', display: { xs: 'none', md: 'flex' } }}
          >
            {SECTIONS.map((section) => (
              <Button
                key={section.href}
                href={section.href}
                sx={{
                  color: landing.textMuted,
                  fontWeight: 600,
                  px: 1.75,
                  '&:hover': { color: landing.text, backgroundColor: 'transparent' },
                }}
              >
                {section.label}
              </Button>
            ))}
          </Stack>

          <Button
            component={RouterLink}
            to="/sign-in"
            sx={{
              ms: { xs: 'auto', md: 0 },
              background: landingGradient,
              color: '#FFFFFF',
              fontWeight: 700,
              px: 3,
              borderRadius: 99,
              '&:hover': { background: landingGradient, filter: 'brightness(1.08)' },
            }}
          >
            ورود به سامانه
          </Button>
        </Stack>
      </Container>
    </Box>
  );
}

/**
 * The mark and the name.
 *
 * @remarks
 * <b>The product's own logo</b>, the same file the browser tab and the installed application
 * icon use. An earlier version of this header drew a placeholder mark instead, which made the
 * public page the one place in the product showing a logo the product does not have.
 *
 * The 192px asset rather than the 512px one: it renders at 36 points, so the larger file would
 * be five times the bytes for pixels no screen can show. Sized in CSS and given explicit
 * intrinsic dimensions so the header does not reflow when it arrives.
 *
 * No `borderRadius` here: the asset draws its own rounded plate and leaves the corners
 * transparent. Rounding it again in CSS would shave the plate's corners a second time.
 */
function Brand() {
  return (
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em' }}>
        رای‌ویرا
      </Typography>

      <Box
        component="img"
        src="/icon-192.png"
        alt="رای‌ویرا"
        width={192}
        height={192}
        sx={{
          width: 36,
          height: 36,
          filter: 'drop-shadow(0 4px 14px rgba(124, 92, 255, 0.45))',
        }}
      />
    </Stack>
  );
}
